import { Dispatch, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Badge,
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  EllipsisMenuButton,
  Flex,
  Menu,
  MenuContent,
  MenuTrigger,
  Select,
  SelectContent,
  SelectTrigger,
  Spinner,
  Text,
  Tooltip,
} from '@optiaxiom/react';
import {
  IconCircleQuestion,
  IconEye,
  IconLink,
  IconPenToSquare,
  IconPlus,
  IconTrashCan,
} from '@optimizely/axiom-icons';

import { MAX_EVALUATION_SAMPLES } from '@frontend/features/agents/constants';
import type { SpecializedAgentAction } from '@frontend/features/agents/hooks';
import { useInstanceId } from '@frontend/hooks';
import * as agentStyles from '@frontend/styles/form.css';
import type { EvaluationSample, EvaluationSampleCreate, SpecializedAgent } from '@frontend/types';

import {
  useCreateEvaluationSample,
  useDeleteEvaluationSample,
  useGetEvaluationSamples,
  useUpdateEvaluationSample,
} from '../../../../hooks/useEvaluationSamples';

import { styles } from './AgentOutputExamples.css';
import { EvaluationSampleForm } from './EvaluationSampleForm';

type AgentOutputExamplesProps = {
  agentId: string;
  agent: SpecializedAgent;
  dispatch: Dispatch<SpecializedAgentAction>;
  lockedFields?: string[];
  disabled?: boolean;
};

export function AgentOutputExamples({
  agentId,
  agent,
  dispatch,
  lockedFields = [],
  disabled = false,
}: AgentOutputExamplesProps) {
  const { instanceId } = useInstanceId();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isFormOpen, setFormOpen] = useState(false);
  const [editingSample, setEditingSample] = useState<EvaluationSample | undefined>();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [sampleToDelete, setSampleToDelete] = useState<EvaluationSample | null>(null);

  const { data: samples = [], isLoading } = useGetEvaluationSamples(
    instanceId ?? '',
    agentId,
    !!instanceId && !!agentId
  );
  const createMutation = useCreateEvaluationSample(instanceId ?? '', agentId);
  const updateMutation = useUpdateEvaluationSample(instanceId ?? '', agentId);
  const deleteMutation = useDeleteEvaluationSample(instanceId ?? '', agentId);

  const handleAddClick = () => {
    setEditingSample(undefined);
    setFormOpen(true);
  };

  const handleEditClick = (sample: EvaluationSample) => {
    setEditingSample(sample);
    setFormOpen(true);
  };

  const handleSave = (data: EvaluationSampleCreate) => {
    if (editingSample) {
      // Update existing sample
      updateMutation.mutate(
        { sampleId: editingSample.id, data },
        {
          onSuccess: () => {
            setFormOpen(false);
            setEditingSample(undefined);
          },
        }
      );
    } else {
      // Create new sample
      createMutation.mutate(data, {
        onSuccess: () => {
          setFormOpen(false);
          setEditingSample(undefined);
        },
      });
    }
  };

  const handleDeleteClick = (sample: EvaluationSample) => {
    setSampleToDelete(sample);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (sampleToDelete) {
      deleteMutation.mutate(sampleToDelete.id, {
        onSuccess: () => {
          setDeleteDialogOpen(false);
          setSampleToDelete(null);
        },
      });
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setSampleToDelete(null);
  };

  const handleViewClick = (sample: EvaluationSample) => {
    if (sample.execution_id) {
      // Navigate to Logs tab with the execution selected
      searchParams.set('tab', 'execution');
      searchParams.set('executionId', sample.execution_id);
      setSearchParams(searchParams);
    }
  };

  if (isLoading) {
    return (
      <Flex alignItems="center" justifyContent="center" py="24">
        <Spinner />
      </Flex>
    );
  }

  return (
    <Flex flexDirection="column" gap="16" w="full" className={agentStyles.FormInputFieldMaxWidth}>
      <Flex flexDirection="column" gap="8" w="full">
        <Flex flexDirection="row" gap="8" alignItems="center">
          <Text fontSize="md" color="fg.default">
            Add your preferred outputs (up to {MAX_EVALUATION_SAMPLES}) to help Opal evaluate its
            response. Varied examples improve assessment quality.
          </Text>
          <Tooltip content="Opal calculates an output quality score by evaluating its response against the provided output examples for correctness, relevance, and tone.">
            <IconCircleQuestion size={16} />
          </Tooltip>
        </Flex>
        <Button
          appearance="default"
          size="md"
          icon={<IconPlus size="16" />}
          onClick={handleAddClick}
          disabled={
            disabled ||
            samples.length >= MAX_EVALUATION_SAMPLES ||
            lockedFields.includes('output_examples')
          }
          alignSelf="end"
        >
          Add Example
        </Button>
      </Flex>

      {samples.length === 0 ? null : (
        <Flex flexDirection="column" gap="12">
          {samples.map((sample, index) => (
            <Flex key={sample.id} className={styles.sampleCard} flexDirection="row" gap="8">
              <Flex flexDirection="column" gap="8" style={{ flex: 1 }}>
                <Flex flexDirection="row" gap="8" alignItems="center">
                  <Badge intent="neutral" variant="strong">
                    {index + 1}
                  </Badge>
                  <Text fontWeight="500">{sample.name}</Text>
                  {sample.execution_id && (
                    <Tooltip content="Linked from execution log">
                      <IconLink size={16} />
                    </Tooltip>
                  )}
                </Flex>
                {sample.description && (
                  <Text fontSize="sm" color="fg.secondary">
                    {sample.description}
                  </Text>
                )}
              </Flex>
              <Menu
                options={[
                  // Only allow editing manually created samples (not execution-linked)
                  ...(!sample.execution_id
                    ? [
                        {
                          label: 'Edit',
                          intent: 'neutral' as const,
                          addon: <IconPenToSquare size={16} />,
                          execute: () => handleEditClick(sample),
                        },
                      ]
                    : []),
                  // Show View option for execution-linked samples
                  ...(sample.execution_id
                    ? [
                        {
                          label: 'View',
                          intent: 'neutral' as const,
                          addon: <IconEye size={16} />,
                          execute: () => handleViewClick(sample),
                        },
                      ]
                    : []),
                  {
                    label: 'Delete',
                    intent: 'danger' as const,
                    addon: <IconTrashCan size={16} />,
                    execute: () => handleDeleteClick(sample),
                  },
                ]}
              >
                <MenuTrigger
                  asChild
                  disabled={disabled || lockedFields.includes('output_examples')}
                >
                  <EllipsisMenuButton appearance="subtle" aria-label="Actions" />
                </MenuTrigger>
                <MenuContent />
              </Menu>
            </Flex>
          ))}
        </Flex>
      )}

      {samples.length > 0 && (
        <Flex flexDirection="column" gap="8" w="full">
          <Flex flexDirection="row" gap="8" alignItems="center">
            <Text fontSize="md" color="fg.default">
              Set your baseline quality score to inform Opal&apos;s execution.
            </Text>
            <Tooltip content="The lowest acceptable quality percentage for this agent's response. If the agent's output quality falls below this score, Opal will flag it in the execution logs.">
              <IconCircleQuestion size={16} />
            </Tooltip>
          </Flex>
          <Select
            disabled={disabled}
            options={Array.from({ length: 10 }, (_, i) => (i + 1) * 10).map(score => ({
              label: `${score}%`,
              value: String(score),
            }))}
            value={agent.baseline_evaluation_score?.toString() ?? '80'}
            onValueChange={value =>
              dispatch({
                type: 'UPDATE_FIELD',
                field: 'baseline_evaluation_score',
                value: value ? Number(value) : undefined,
              })
            }
          >
            <SelectTrigger w="224" />
            <SelectContent />
          </Select>
        </Flex>
      )}

      <EvaluationSampleForm
        initialValues={editingSample}
        agentParameters={agent.parameters || []}
        onSave={handleSave}
        onCancel={() => {
          setFormOpen(false);
          setEditingSample(undefined);
        }}
        open={isFormOpen}
        onOpenChange={setFormOpen}
        isSaving={createMutation.isPending || updateMutation.isPending}
      />

      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>Delete Example</DialogHeader>
          <DialogBody>
            <Text>
              Are you sure you want to delete the example &quot;{sampleToDelete?.name}&quot;? This
              action cannot be undone.
            </Text>
          </DialogBody>
          <DialogFooter>
            <Flex flexDirection="row" gap="8" justifyContent="flex-end" w="full">
              <Button
                appearance="default"
                onClick={handleCancelDelete}
                disabled={deleteMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                appearance="danger"
                onClick={handleConfirmDelete}
                loading={deleteMutation.isPending}
              >
                Delete
              </Button>
            </Flex>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Flex>
  );
}
